import { Factory, X } from "lucide-react";
import { industryColor, industryCounts, industryOf } from "../data/employer";

// Compact employers overlay, toggled from the map control stack alongside the
// contact-marker and lifecycle buttons. Industry multi-select + roster.
export const EmployersPanel = ({
  employers,
  industryFilter, // Set<string> | null (null = all)
  setIndustryFilter,
  selectedId,
  onSelect,
  onClose,
}) => {
  const counts = industryCounts(employers);
  const isAll = !industryFilter || industryFilter.size === 0;
  const toggle = (name) => {
    const next = new Set(industryFilter || []);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    setIndustryFilter(next.size === 0 ? null : next);
  };
  const shown = isAll
    ? employers
    : employers.filter((e) => industryFilter.has(industryOf(e)));

  return (
    <div
      className="absolute z-[820] top-[188px] right-4 w-[min(92vw,20rem)] lg:w-[24rem] xl:w-[30rem] max-h-[58vh] xl:max-h-[72vh] overflow-auto border border-white/10 bg-[#0a0a0c]/95 backdrop-blur-sm"
      data-testid="employers-panel"
    >
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/10">
        <Factory className="w-3.5 h-3.5 xl:w-4 xl:h-4 text-amber-300 shrink-0" />
        <span className="font-mono text-[12px] xl:text-[13px] tracking-[0.18em] uppercase text-zinc-300 truncate">
          Employer customers
        </span>
        <span
          className="ml-auto font-mono text-[12px] xl:text-[13px] text-zinc-500"
          data-testid="employers-count"
        >
          {shown.length}/{employers.length}
        </span>
        <button
          type="button"
          onClick={onClose}
          data-testid="employers-close"
          aria-label="Hide employers"
          className="text-zinc-500 hover:text-zinc-200"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="px-3 xl:px-4 py-2 xl:py-3 space-y-1 xl:space-y-1.5" data-testid="industry-filter">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[11px] xl:text-[13px] tracking-[0.18em] uppercase text-zinc-500">
            // Industry
          </span>
          <button
            type="button"
            onClick={() => setIndustryFilter(null)}
            data-testid="industry-filter-all"
            className={`font-mono text-[11px] xl:text-[13px] tracking-[0.16em] uppercase ${
              isAll ? "text-amber-300" : "text-zinc-500 hover:text-zinc-300"
            }`}
          >
            All
          </button>
        </div>
        {counts.length === 0 && (
          <div className="font-mono text-[12px] xl:text-[13px] text-zinc-600" data-testid="industry-empty">
            No employer records yet — they arrive with the HubSpot pull.
          </div>
        )}
        {counts.map(([name, count]) => {
          const on = isAll || industryFilter.has(name);
          return (
            <button
              key={name}
              type="button"
              onClick={() => toggle(name)}
              data-testid={`industry-option-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              aria-pressed={on}
              className={`w-full flex items-center gap-2 px-2 xl:px-2.5 py-1.5 xl:py-2 border text-left transition-colors ${
                on ? "border-white/20 bg-white/[0.04]" : "border-transparent hover:bg-white/[0.02]"
              }`}
            >
              <span
                className="w-2.5 h-2.5 xl:w-3 xl:h-3 shrink-0 rounded-full"
                style={{
                  backgroundColor: on ? industryColor(name) : "transparent",
                  border: `1px solid ${industryColor(name)}`,
                }}
              />
              <span className={`font-mono text-[12px] xl:text-[14px] truncate ${on ? "text-zinc-200" : "text-zinc-500"}`}>
                {name}
              </span>
              <span className="ml-auto font-mono text-[12px] xl:text-[13px] text-zinc-500">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="border-t border-white/10 py-1" data-testid="employers-list">
        {shown.map((e) => (
          <button
            key={e.id}
            type="button"
            onClick={() => onSelect(e.id)}
            data-testid={`employer-row-${e.id}`}
            className={`w-full text-left px-3 xl:px-4 py-2 xl:py-2.5 border-l-2 transition-colors ${
              selectedId === e.id
                ? "border-amber-400 bg-amber-500/10"
                : "border-transparent hover:bg-white/[0.03]"
            }`}
          >
            <div className="flex items-center gap-2">
              <span className="text-[13px] xl:text-[15px] text-zinc-100 truncate">{e.name}</span>
              <span
                className="ml-auto font-mono text-[11px] xl:text-[13px] px-1 xl:px-1.5 py-0.5 border shrink-0"
                style={{ color: industryColor(industryOf(e)), borderColor: industryColor(industryOf(e)) }}
              >
                {industryOf(e)}
              </span>
            </div>
            <div className="font-mono text-[12px] xl:text-[13px] text-zinc-500 truncate">
              {[e.city, e.state].filter(Boolean).join(", ") || "—"}
              {e.employees ? ` · ${e.employees} employees` : ""}
              {!e.coords ? " · unmapped" : ""}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
